import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    AlertCircle,
    CheckCircle2,
    HardDrive,
    Link2,
    Loader2,
    Unlink,
} from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import {
    connectDrive,
    disconnectDrive,
    fetchDriveStatus,
} from "../../../services/api";
import styles from "./PainelIntegracoes.module.css";

const CALLBACK_MESSAGES = {
    conectado: { type: "ok", text: "Google Drive conectado com sucesso!" },
    sem_refresh_token: {
        type: "erro",
        text: "O Google não retornou um token. Tente conectar novamente.",
    },
    state_invalido: { type: "erro", text: "Sessão de conexão inválida. Tente de novo." },
    oauth_indisponivel: {
        type: "erro",
        text: "OAuth não está configurado no servidor (Client ID/Secret).",
    },
    falha_token: { type: "erro", text: "Falha ao concluir a conexão com o Google." },
};

export default function PainelIntegracoes() {
    const { password } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();

    const [status, setStatus] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isWorking, setIsWorking] = useState(false);
    const [banner, setBanner] = useState(null);

    const loadStatus = useCallback(async () => {
        try {
            setIsLoading(true);
            const data = await fetchDriveStatus(password);
            setStatus(data);
        } catch (err) {
            setBanner({ type: "erro", text: err?.message || "Falha ao carregar status." });
        } finally {
            setIsLoading(false);
        }
    }, [password]);

    useEffect(() => {
        loadStatus();
    }, [loadStatus]);

    // Lê o resultado do redirect do OAuth (?drive=ok|erro&msg=...) e limpa a URL.
    useEffect(() => {
        const drive = searchParams.get("drive");
        if (!drive) return;

        const msg = searchParams.get("msg") || "";
        const mapped = CALLBACK_MESSAGES[msg];
        if (mapped) {
            setBanner(mapped);
        } else {
            setBanner(
                drive === "ok"
                    ? { type: "ok", text: "Google Drive conectado." }
                    : { type: "erro", text: "Não foi possível conectar o Google Drive." }
            );
        }

        searchParams.delete("drive");
        searchParams.delete("msg");
        setSearchParams(searchParams, { replace: true });
    }, [searchParams, setSearchParams]);

    const handleConnect = async () => {
        try {
            setIsWorking(true);
            setBanner(null);
            const { auth_url: authUrl } = await connectDrive(password);
            if (authUrl) {
                window.location.href = authUrl;
            } else {
                setBanner({ type: "erro", text: "URL de autorização não recebida." });
                setIsWorking(false);
            }
        } catch (err) {
            setBanner({ type: "erro", text: err?.message || "Falha ao iniciar a conexão." });
            setIsWorking(false);
        }
    };

    const handleDisconnect = async () => {
        if (!window.confirm("Desconectar o Google Drive? Novos uploads deixarão de ir para o Drive.")) {
            return;
        }
        try {
            setIsWorking(true);
            setBanner(null);
            await disconnectDrive(password);
            await loadStatus();
            setBanner({ type: "ok", text: "Google Drive desconectado." });
        } catch (err) {
            setBanner({ type: "erro", text: err?.message || "Falha ao desconectar." });
        } finally {
            setIsWorking(false);
        }
    };

    const connected = status?.connected;
    const configured = status?.configured;

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <span className={styles.eyebrow}>Integrações</span>
                <h1>Conexões externas</h1>
                <p>
                    Conecte serviços usados pelo blog. As imagens enviadas pelo painel e pelo
                    agente são armazenadas no destino conectado aqui.
                </p>
            </header>

            {banner && (
                <div
                    className={`${styles.banner} ${
                        banner.type === "ok" ? styles.bannerOk : styles.bannerErro
                    }`}
                >
                    {banner.type === "ok" ? (
                        <CheckCircle2 size={18} aria-hidden="true" />
                    ) : (
                        <AlertCircle size={18} aria-hidden="true" />
                    )}
                    <span>{banner.text}</span>
                </div>
            )}

            <div className={styles.card}>
                <div className={styles.cardIcon}>
                    <HardDrive size={26} aria-hidden="true" />
                </div>

                <div className={styles.cardBody}>
                    <div className={styles.cardTitleRow}>
                        <h2>Google Drive</h2>
                        {isLoading ? (
                            <span className={`${styles.pill} ${styles.pillNeutral}`}>
                                <Loader2 size={13} className={styles.spinner} aria-hidden="true" />
                                Verificando…
                            </span>
                        ) : connected ? (
                            <span className={`${styles.pill} ${styles.pillOk}`}>
                                <CheckCircle2 size={13} aria-hidden="true" /> Conectado
                            </span>
                        ) : (
                            <span className={`${styles.pill} ${styles.pillOff}`}>
                                Não conectado
                            </span>
                        )}
                    </div>

                    <p className={styles.cardDesc}>
                        Armazena as imagens do blog na sua conta do Google Drive. As URLs ficam
                        públicas e persistem entre deploys.
                    </p>

                    {!isLoading && connected && status?.email && (
                        <p className={styles.connectedAs}>
                            Conectado como <strong>{status.email}</strong>
                        </p>
                    )}

                    {!isLoading && !configured && (
                        <p className={styles.warnText}>
                            <AlertCircle size={15} aria-hidden="true" />
                            O servidor ainda não tem as credenciais OAuth (Client ID/Secret)
                            configuradas.
                        </p>
                    )}

                    {!isLoading && !connected && configured && status?.fallback_service_account && (
                        <p className={styles.infoText}>
                            Há uma Service Account configurada como alternativa, mas conectar sua
                            conta é o método recomendado.
                        </p>
                    )}

                    <div className={styles.actions}>
                        {connected ? (
                            <button
                                type="button"
                                className={styles.secondaryButton}
                                onClick={handleDisconnect}
                                disabled={isWorking}
                            >
                                {isWorking ? (
                                    <Loader2 size={16} className={styles.spinner} aria-hidden="true" />
                                ) : (
                                    <Unlink size={16} aria-hidden="true" />
                                )}
                                Desconectar
                            </button>
                        ) : (
                            <button
                                type="button"
                                className={styles.primaryButton}
                                onClick={handleConnect}
                                disabled={isWorking || !configured}
                            >
                                {isWorking ? (
                                    <Loader2 size={16} className={styles.spinner} aria-hidden="true" />
                                ) : (
                                    <Link2 size={16} aria-hidden="true" />
                                )}
                                Conectar Google Drive
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
